import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

/**
 * Acquisition par SOURCE : comptes créés et jalons franchis sur la période, rangés
 * par le premier contact du compte (convention UTM v2). La définition fait foi au
 * serveur (`admin.queries` → `getAcquisitionParSource`) : la source se lit à la
 * FAMILLE (compte le plus ancien qui en a une), si bien qu'un parent payant est
 * rangé sous le lien qui a amené son enfant. Cet écran ne recalcule rien.
 */

const ETAPES = [
  { cle: 'compte', libelle: 'Comptes créés' },
  { cle: 'combat_1', libelle: '1er combat' },
  { cle: 'onboarding', libelle: 'Onboarding' },
  { cle: 'identifie', libelle: 'Identifiés' },
  { cle: 'payant', libelle: 'Payants' },
];

const NON_MESUREE = 'Non mesurée';

const pct = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)} %` : '—');

/** Pivote la réponse longue en une ligne par (source, medium[, campagne]). */
function pivoter(lignes, parCampagne) {
  const table = new Map();
  for (const l of lignes) {
    const campagne = parCampagne ? l.campagne || '' : '';
    const cle = `${l.source ?? ''}|${l.medium ?? ''}|${campagne}`;
    if (!table.has(cle)) {
      table.set(cle, { cle, source: l.source, medium: l.medium, campagne, compte: 0, combat_1: 0, onboarding: 0, identifie: 0, payant: 0 });
    }
    table.get(cle)[l.etape] += l.nombre;
  }
  return [...table.values()].sort((a, b) =>
    // La source non mesurée en dernier : elle n'arbitre rien.
    (a.source === null) - (b.source === null) || b.payant - a.payant || b.compte - a.compte,
  );
}

export default function AcquisitionSourcesSection({ days = 30, segment = 'familles' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parCampagne, setParCampagne] = useState(false);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getAcquisitionBySource(days, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching acquisition by source:', err);
        if (!annule) setError("Impossible de charger l'acquisition par source");
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [days, segment]);

  const lignes = useMemo(() => pivoter(data?.lignes ?? [], parCampagne), [data, parCampagne]);

  return (
    <Card>
      <CardHeader
        title="Acquisition par source"
        subheader={`Comptes créés et jalons franchis sur ${days} jours, rangés par le premier contact de la famille`}
        action={(
          <FormControlLabel
            control={<Switch checked={parCampagne} onChange={(e) => setParCampagne(e.target.checked)} />}
            label="Détail par campagne"
          />
        )}
      />
      <CardContent>
        {loading && <Box display="flex" justifyContent="center" p={2}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && lignes.length === 0 && (
          <Typography variant="body2" color="text.secondary">Aucun compte sur la période.</Typography>
        )}
        {!loading && !error && lignes.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Medium</TableCell>
                  {parCampagne && <TableCell>Campagne</TableCell>}
                  {ETAPES.map((e) => <TableCell key={e.cle} align="right">{e.libelle}</TableCell>)}
                  <TableCell align="right">Comptes → payants</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lignes.map((l) => (
                  <TableRow key={l.cle} sx={l.source === null ? { opacity: 0.6 } : undefined}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.source ?? NON_MESUREE}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.medium ?? ''}</TableCell>
                    {parCampagne && <TableCell sx={{ fontFamily: 'monospace' }}>{l.campagne}</TableCell>}
                    {ETAPES.map((e) => (
                      <TableCell key={e.cle} align="right">{l[e.cle].toLocaleString('fr-FR')}</TableCell>
                    ))}
                    <TableCell align="right">{pct(l.payant, l.compte)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Chaque colonne compte ce qui s&apos;est passé DANS la période : un payant de ce mois peut venir
          d&apos;un compte créé avant. « {NON_MESUREE} » = familles créées avant la mesure (migration 216).
          « dicto.fr / site » = passé par le site, origine perdue ; « direct » = arrivé sans lien ni référent.
        </Typography>
      </CardContent>
    </Card>
  );
}
